import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import useNotificationHandler from "hooks/useNotificationHandler";
import api from "service/api";
import { QUERY_KEYS } from "library/shared";
import { CalculationRequestDto } from "./useCalculations";
import {
  OfferListEntryDto,
  QuoteUpdateDto,
  ExcludedItemDto,
} from "library/types/types";

// Types based on the API collection structure
export interface OfferRequestDto {
  CalculationId: string;
  SelectedInsurers?: string[];
  // Add other properties as needed based on your API structure
}

export interface OfferResponseDto {
  OfferId: string;
  CalculationId?: string;
  Status?: string;
  CreatedDate?: string;
  // Add other response properties as needed based on your API response
}

export interface OfferListItemDto {
  OfferId: string;
  CalculationId?: string;
  Status?: string;
  CreatedDate?: string;
  // Add other properties for list view
}

const generateOffer = async (
  data: OfferRequestDto,
): Promise<OfferResponseDto> => {
  const response = await api.post(`/offers`, data);
  return response.data;
};

// New function: Create offer from calculation request (replaces POST /api/calculations)
const createOffer = async (
  data: CalculationRequestDto,
): Promise<OfferListEntryDto> => {
  console.log("=== Sending Create Offer API Request ===");
  console.log("Request data:", data);
  console.log("Mtpl.SelectedInsurers:", data.Mtpl?.SelectedInsurers);
  console.log("Casco.SelectedInsurers:", data.Casco?.SelectedInsurers);
  console.log("SelectedProducts:", data.SelectedProducts);

  const response = await api.post(`/offers`, data);
  return response.data;
};

const getOfferData = async (offerId: string): Promise<OfferResponseDto> => {
  const response = await api.get(`/offers/${offerId}`);
  return response.data;
};

const getUserOffers = async (): Promise<OfferListItemDto[]> => {
  const response = await api.get(`/offers`);
  return response.data;
};

const invalidateOffer = async (offerId: string): Promise<void> => {
  const response = await api.patch(`/offers/invalidate/${offerId}`);
  return response.data;
};

/**
 * Mark the offer as error entry (admins only, any status) or unmark it.
 * While marked, no other operation than unmark is possible on the offer.
 *
 * API Endpoint: PATCH /offers/markaserror/{offerId}/{markAsError}
 */
const setOfferMarkedAsError = async ({
  offerId,
  markAsError,
}: {
  offerId: string;
  markAsError: boolean;
}): Promise<void> => {
  const response = await api.patch(
    `/offers/markaserror/${offerId}/${markAsError}`,
  );
  return response.data;
};

/**
 * Mark invoice as paid
 * @param offerId - The ID of the offer
 * @param invoiceNumber - The number of the invoice to mark as paid (available in the search row)
 * @returns Promise that resolves when the invoice is marked as paid
 *
 * API Endpoint: PATCH /invoices/markaspaid/{offerId}/{invoiceNumber}
 */
const markInvoiceAsPaid = async (
  offerId: string,
  invoiceNumber: string,
): Promise<void> => {
  const response = await api.patch(
    `/invoices/markaspaid/${offerId}/${encodeURIComponent(invoiceNumber)}`,
  );
  return response.data;
};

/**
 * Mark paid invoice as issued (policy issued outside QTG)
 * @param offerId - The ID of the offer
 * @param invoiceNumber - The number of the paid invoice
 * @returns Promise that resolves when the offer is marked as policy issued
 *
 * API Endpoint: PATCH /invoices/markasissued/{offerId}/{invoiceNumber}
 */
const markPaidInvoiceAsIssued = async (
  offerId: string,
  invoiceNumber: string,
): Promise<void> => {
  const response = await api.patch(
    `/invoices/markasissued/${offerId}/${encodeURIComponent(invoiceNumber)}`,
  );
  return response.data;
};

/**
 * Save offer
 * @param offerId - The ID of the offer to save
 * @returns Promise<OfferListEntryDto> - Updated offer object with new status
 *
 * API Endpoint: PATCH /offers/save/{offerId}
 */
const saveOffer = async (offerId: string): Promise<OfferListEntryDto> => {
  const response = await api.patch(`/offers/save/${offerId}`);
  return response.data;
};

/**
 * Update offer with modified quote values and exclusions
 * @param offerId - The ID of the offer to update
 * @param data - Updated MTPL and CASCO quotes, and exclusions
 * @returns Promise<OfferListEntryDto> - Updated offer object with regenerated documents
 *
 * API Endpoint: PATCH /offers/{offerId}
 */
const updateOffer = async (
  offerId: string,
  data: {
    MtplUpdated?: QuoteUpdateDto[];
    CascoUpdated?: QuoteUpdateDto[];
    MtplExcluded?: ExcludedItemDto[];
    CascoExcluded?: ExcludedItemDto[];
    CascoIncludeAdditionalConditions?: boolean;
  },
): Promise<OfferListEntryDto> => {
  console.log("=== Sending Update Offer API Request ===");
  console.log("Offer ID:", offerId);
  console.log("MtplUpdated:", data.MtplUpdated);
  console.log("CascoUpdated:", data.CascoUpdated);
  console.log("MtplExcluded:", data.MtplExcluded);
  console.log("CascoExcluded:", data.CascoExcluded);

  const response = await api.patch(`/offers/${offerId}`, data);
  return response.data;
};

export interface SendEmailRequestDto {
  from: {
    name: string;
    email: string;
  };
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
  attachments: Array<{
    name: string;
    url?: string; // URL for existing proposal documents
    file?: string; // Base64 encoded file content for uploads
  }>;
}

/**
 * Send offer via email
 * @param offerId - The ID of the offer to send
 * @param data - Email details and attachments
 * @returns Promise<OfferListEntryDto> - Updated offer object with new status
 *
 * API Endpoint: PATCH /offers/send/{offerId}
 */
const sendOfferEmail = async (
  offerId: string,
  data: SendEmailRequestDto,
): Promise<OfferListEntryDto> => {
  const response = await api.patch(`/offers/send/${offerId}`, data);
  return response.data;
};

/**
 * Send invoice via email
 * @param offerId - The ID of the offer
 * @param invoiceId - The ID of the invoice to send
 * @param data - Email details and attachments
 * @returns Promise<OfferListEntryDto> - Updated offer object with new status
 *
 * API Endpoint: PATCH /invoices/send/{offerId}/{invoiceId}
 */
const sendInvoiceEmail = async (
  offerId: string,
  invoiceId: string,
  data: SendEmailRequestDto,
): Promise<OfferListEntryDto> => {
  const response = await api.patch(
    `/invoices/send/${offerId}/${invoiceId}`,
    data,
  );
  return response.data;
};

export function useOfferMutation(
  onSuccessCallback?: (data: OfferResponseDto) => void,
) {
  const { t } = useTranslation();
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  return useMutation<OfferResponseDto, Error, OfferRequestDto>({
    mutationFn: generateOffer,
    onSuccess: (offerData) => {
      toast.success(t("qtg.offers.generateSuccess"));
      if (onSuccessCallback) {
        onSuccessCallback(offerData);
      }
    },
    onError: (error: any) => {
      handleAddAxiosErrorNotification(error);
    },
  });
}

export function useOfferQuery(
  offerId: string,
  options?: {
    enabled?: boolean;
    onSuccess?: (data: OfferResponseDto) => void;
    onError?: (error: Error) => void;
  },
) {
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  const result = useQuery<OfferResponseDto, Error>({
    queryKey: [QUERY_KEYS.offers, offerId],
    queryFn: () => getOfferData(offerId),
    enabled: options?.enabled ?? !!offerId,
  });

  useEffect(() => {
    if (result.isSuccess && options?.onSuccess) {
      options.onSuccess(result.data);
    }
    if (result.error) {
      handleAddAxiosErrorNotification(result.error);
      options?.onError?.(result.error);
    }
  }, [
    result.isSuccess,
    result.error,
    result.data,
    options,
    handleAddAxiosErrorNotification,
  ]);

  return result;
}

export function useOffersListQuery(options?: {
  enabled?: boolean;
  onSuccess?: (data: OfferListItemDto[]) => void;
  onError?: (error: Error) => void;
}) {
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  const result = useQuery<OfferListItemDto[], Error>({
    queryKey: [QUERY_KEYS.offers, "list"],
    queryFn: getUserOffers,
    enabled: options?.enabled,
  });

  useEffect(() => {
    if (result.isSuccess && options?.onSuccess) {
      options.onSuccess(result.data);
    }
    if (result.error) {
      handleAddAxiosErrorNotification(result.error);
      options?.onError?.(result.error);
    }
  }, [
    result.isSuccess,
    result.error,
    result.data,
    options,
    handleAddAxiosErrorNotification,
  ]);

  return result;
}

export function useInvalidateOfferMutation(onSuccessCallback?: () => void) {
  const { t } = useTranslation();
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  return useMutation<void, Error, string>({
    mutationFn: invalidateOffer,
    onSuccess: () => {
      toast.success(t("qtg.offers.invalidateSuccess"));
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    },
    onError: (error: any) => {
      handleAddAxiosErrorNotification(error);
    },
  });
}

export function useMarkOfferAsErrorMutation(onSuccessCallback?: () => void) {
  const { t } = useTranslation();
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  return useMutation<void, Error, { offerId: string; markAsError: boolean }>({
    mutationFn: setOfferMarkedAsError,
    onSuccess: (_, { markAsError }) => {
      toast.success(
        markAsError
          ? t("qtg.offers.markAsErrorSuccess", "Offer marked as error")
          : t("qtg.offers.unmarkAsErrorSuccess", "Offer is no longer marked as error"),
      );
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    },
    onError: (error: any) => {
      handleAddAxiosErrorNotification(error);
    },
  });
}

/**
 * Hook for marking an invoice as paid
 * @param onSuccessCallback - Callback to execute on success
 * @returns Mutation hook for marking invoice as paid
 */
export function useMarkInvoiceAsPaidMutation(onSuccessCallback?: () => void) {
  const { t } = useTranslation();
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  return useMutation<void, Error, { offerId: string; invoiceNumber: string }>({
    mutationFn: ({ offerId, invoiceNumber }) =>
      markInvoiceAsPaid(offerId, invoiceNumber),
    onSuccess: () => {
      toast.success(
        t(
          "qtg.offers.markInvoiceAsPaidSuccess",
          "Invoice marked as paid successfully",
        ),
      );
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    },
    onError: (error: any) => {
      handleAddAxiosErrorNotification(error);
    },
  });
}

/**
 * Hook for marking a paid invoice as issued (offer status -> PolicyIssued)
 * @param onSuccessCallback - Callback to execute on success
 * @returns Mutation hook for marking paid invoice as issued
 */
export function useMarkPaidInvoiceAsIssuedMutation(
  onSuccessCallback?: () => void,
) {
  const { t } = useTranslation();
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  return useMutation<void, Error, { offerId: string; invoiceNumber: string }>({
    mutationFn: ({ offerId, invoiceNumber }) =>
      markPaidInvoiceAsIssued(offerId, invoiceNumber),
    onSuccess: () => {
      toast.success(
        t(
          "qtg.offers.markPaidInvoiceAsIssuedSuccess",
          "Invoice marked as issued successfully",
        ),
      );
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    },
    onError: (error: any) => {
      handleAddAxiosErrorNotification(error);
    },
  });
}

/**
 * Hook for saving an offer
 * @param onSuccessCallback - Callback to execute on success with updated offer data
 * @returns Mutation hook for saving an offer
 */
export function useSaveOfferMutation(
  onSuccessCallback?: (data: OfferListEntryDto) => void,
) {
  const { t } = useTranslation();
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  return useMutation<OfferListEntryDto, Error, string>({
    mutationFn: saveOffer,
    onSuccess: (data) => {
      toast.success(t("qtg.offers.saveSuccess", "Offer saved successfully!"));
      if (onSuccessCallback) {
        onSuccessCallback(data);
      }
    },
    onError: (error: any) => {
      // Handle specific error codes
      if (error?.response?.status === 403) {
        toast.error(
          t(
            "qtg.offers.saveError.unauthorized",
            "Not authorized to save offer",
          ),
        );
      } else if (error?.response?.status === 404) {
        toast.error(t("qtg.offers.saveError.notFound", "Offer not found"));
      } else if (error?.response?.status === 409) {
        toast.error(
          t(
            "qtg.offers.saveError.conflict",
            "Offer already saved, invalidated, or archived",
          ),
        );
      } else {
        handleAddAxiosErrorNotification(error);
      }
    },
  });
}

// New hook: Create offer mutation (replaces useCalculationMutation)
export function useCreateOfferMutation(
  onSuccessCallback?: (data: OfferListEntryDto) => void,
) {
  const { t } = useTranslation();
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  return useMutation<OfferListEntryDto, Error, CalculationRequestDto>({
    mutationFn: createOffer,
    onSuccess: (offerData) => {
      toast.success(t("qtg.calculation.success"));
      if (onSuccessCallback) {
        onSuccessCallback(offerData);
      }
    },
    onError: (error: any) => {
      handleAddAxiosErrorNotification(error);
    },
  });
}

/**
 * Hook for updating offer with modified quote values and exclusions
 * @param offerId - The ID of the offer to update
 * @param onSuccessCallback - Callback to execute on success with updated offer data
 * @returns Mutation hook for updating an offer
 */
export function useUpdateOfferMutation(
  offerId: string,
  onSuccessCallback?: (data: OfferListEntryDto) => void,
) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  return useMutation<
    OfferListEntryDto,
    Error,
    {
      MtplUpdated?: QuoteUpdateDto[];
      CascoUpdated?: QuoteUpdateDto[];
      MtplExcluded?: ExcludedItemDto[];
      CascoExcluded?: ExcludedItemDto[];
      CascoIncludeAdditionalConditions?: boolean;
    }
  >({
    mutationFn: (data) => updateOffer(offerId, data),
    onSuccess: (updatedOffer) => {
      // Invalidate the offer query to refetch
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.offer, offerId] });
      toast.success(
        t("qtg.proposal.updateOffer.success", "Offer updated successfully"),
      );
      if (onSuccessCallback) {
        onSuccessCallback(updatedOffer);
      }
    },
    onError: (error: any) => {
      toast.error(
        t("qtg.proposal.updateOffer.error", "Failed to update offer"),
      );
      handleAddAxiosErrorNotification(error);
    },
  });
}

export function useSendOfferEmailMutation(
  offerId: string,
  onSuccessCallback?: (updatedOffer: OfferListEntryDto) => void,
) {
  const { t } = useTranslation();
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  return useMutation<OfferListEntryDto, Error, SendEmailRequestDto>({
    mutationFn: (data) => sendOfferEmail(offerId, data),
    onSuccess: (updatedOffer) => {
      toast.success(t("qtg.email.sendSuccess", "Email sent successfully"));
      if (onSuccessCallback) {
        onSuccessCallback(updatedOffer);
      }
    },
    onError: (error: any) => {
      toast.error(
        t("qtg.email.sendError", "Failed to send email. Please try again."),
      );
      handleAddAxiosErrorNotification(error);
    },
  });
}

export function useSendInvoiceEmailMutation(
  offerId: string,
  invoiceId: string,
  onSuccessCallback?: (updatedOffer: OfferListEntryDto) => void,
) {
  const { t } = useTranslation();
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  return useMutation<OfferListEntryDto, Error, SendEmailRequestDto>({
    mutationFn: (data) => sendInvoiceEmail(offerId, invoiceId, data),
    onSuccess: (updatedOffer) => {
      toast.success(t("qtg.email.sendSuccess", "Email sent successfully"));
      if (onSuccessCallback) {
        onSuccessCallback(updatedOffer);
      }
    },
    onError: (error: any) => {
      toast.error(
        t("qtg.email.sendError", "Failed to send email. Please try again."),
      );
      handleAddAxiosErrorNotification(error);
    },
  });
}
